import {
    Component,
    Input,
    type OnDestroy,
    type OnInit,
    inject,
} from "@angular/core";
import { HeaderComponent } from "@coreui/angular";
import { NgbModal } from "@ng-bootstrap/ng-bootstrap";
import { type Observable, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { AccountService, type IUser, LoginService } from "../../../core";
import { ClientMessageService } from "../../../entities/message/service/client-message.service";
import { UserAppNotificationDetailModalComponent } from "../../../entities/user-app-notification/component/detail/user-app-notification-detail-modal.component";
import type { IUserAppNotification } from "../../../entities/user-app-notification/model/user-app-notification.model";
import { UserAppNotificationService } from "../../../entities/user-app-notification/service/user-app-notification.service";
import { Event, EventService } from "../../../shared/events/events.service";

@Component({
    selector: "app-default-header",
    templateUrl: "./default-header.component.html",
    styleUrls: ["./default-header.component.scss"],
    standalone: false,
})
export class DefaultHeaderComponent
    extends HeaderComponent
    implements OnInit, OnDestroy
{
    private accountService = inject(AccountService);
    private loginService = inject(LoginService);
    private eventService = inject(EventService);
    private modalService = inject(NgbModal);
    private clientMessageService = inject(ClientMessageService);
    private userAppNotificationService = inject(UserAppNotificationService);

    currentAccount: IUser;
    currentUserIsAdmin: boolean;
    currentUserIsLimitedAccess: boolean;
    showClientMessages: boolean;
    unreadMessagesCount$: Observable<number>;
    userAppNotifications: IUserAppNotification[] = [];
    unreadUserAppNotificationsCount: number = 0;

    destroyed$ = new Subject<void>();

    @Input() sidebarId: string = "sidebar";
    @Input() hidenav: boolean;

    ngOnInit(): void {
        this.eventService.on(Event.REFRESH_NAV, () => this.refreshNav());
        this.accountService.userIdentity.subscribe(() => this.refreshNav());
        this.refreshNav();

        this.userAppNotificationService.onUpdate
            .pipe(takeUntil(this.destroyed$))
            .subscribe(() => this.loadUserAppNotifications());
        this.loadUserAppNotifications();
    }

    ngOnDestroy(): void {
        this.destroyed$.next();
        this.destroyed$.complete();
    }

    refreshNav(): void {
        if (!this.hidenav && this.accountService.hasAuthToken()) {
            this.accountService.identity().then((account) => {
                this.currentAccount = account;
                this.currentUserIsAdmin = this.accountService.hasAnyAuthority([
                    "ROLE_ADMIN",
                ]);
                this.currentUserIsLimitedAccess =
                    this.accountService.hasAnyAuthority([
                        "ROLE_LIMITED_ACCESS",
                    ]);

                if (
                    this.currentAccount.firmSettings.enableClientMessages &&
                    this.accountService.hasAnyAuthority([
                        "ROLE_CLIENT_MESSAGES",
                    ])
                ) {
                    this.showClientMessages = true;
                    this.getUnreadMessagesCount();
                } else {
                    this.showClientMessages = false;
                }
            });
        }
    }

    logout(): void {
        this.loginService.logout();
    }

    loadUserAppNotifications(): void {
        this.userAppNotificationService
            .getAll({
                readFlag: false,
                inactive: false,
                page: 0,
                size: 10,
                sort: ["createdDate,desc"],
            })
            .subscribe((getAllModel) => {
                this.userAppNotifications = getAllModel.notifications;
                this.unreadUserAppNotificationsCount = getAllModel.totalCount;
            });
    }

    openNotificationDetailModal(notification: IUserAppNotification): void {
        const modalRef = this.modalService.open(
            UserAppNotificationDetailModalComponent,
            { size: "lg" },
        );
        modalRef.componentInstance.notification = notification;
    }

    private getUnreadMessagesCount(): void {
        this.unreadMessagesCount$ =
            this.clientMessageService.unreadMessagesCount$;
        this.clientMessageService.getUnreadClientMessagesCount().subscribe();
    }
}
